import AddIcon from "@mui/icons-material/Add";
import Button from "@mui/material/Button";
import {
	GridActionsCellItem,
	GridRowEditStopReasons,
	GridToolbarContainer,
} from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { DataGridPro } from "@mui/x-data-grid-pro";
import Box from "@mui/material/Box";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	getFields,
	getFieldsArray,
	getFieldsStatus,
	getUoms,
	getUomsData,
	getUomsStatus,
} from "../../redux/ProductsSlice";
import NewParameterDialog from "../Dialogs/NewParameterDialog";

export default function ProductParameterDataGrid({
	rows,
	setRows,
	isEditMode,
	minWidth,
	isProductDetailsPage,
}) {
	const [rowModesModel, setRowModesModel] = useState({});
	const [openDialog, setOpenDialog] = useState(false);
	const [rowToEdit, setRowToEdit] = useState(null);

	const dispatch = useDispatch();

	function EditToolbar(props) {
		const { setRows, setRowModesModel } = props;

		const handleClickOpen = () => {
			setOpenDialog(true);
		};

		const handleClose = () => {
			setOpenDialog(false);
		};

		const existingFields = useSelector(getFieldsArray);
		const existingFieldsStatus = useSelector(getFieldsStatus);

		useEffect(() => {
			if (existingFieldsStatus === "idle") {
				dispatch(getFields());
			}
		}, [dispatch, existingFieldsStatus]);

		return (
			isEditMode && (
				<>
					<GridToolbarContainer sx={{ border: "0px !important" }}>
						<Button
							color='primary'
							variant='contained'
							startIcon={<AddIcon />}
							sx={{ mb: 2 }}
							onClick={handleClickOpen}
						>
							Add parameter
						</Button>
					</GridToolbarContainer>
					<NewParameterDialog
						openDialog={openDialog}
						handleClose={handleClose}
						rows={rows}
						setRows={setRows}
						setRowModesModel={setRowModesModel}
						initialState={rowToEdit}
						setRowToEdit={setRowToEdit}
						existingFields={existingFields}
						isNew
					/>
				</>
			)
		);
	}

	const uoms = useSelector(getUomsData);
	const uomsStatus = useSelector(getUomsStatus);

	useEffect(() => {
		if (uomsStatus === "idle") {
			dispatch(getUoms());
		}
	}, [dispatch, uomsStatus]);

	const uomOptions = Object.keys(uoms).flatMap((group) =>
		uoms[group].map((option) => ({ ...option, group }))
	);

	const handleRowEditStop = (params, event) => {
		if (params.reason === GridRowEditStopReasons.rowFocusOut) {
			event.defaultMuiPrevented = true;
		}
	};

	const handleEditClick = (name) => () => {
		setOpenDialog(true);
		setRowToEdit(rows.find((row) => row.name === name));
	};

	const handleDeleteClick = (name) => () => {
		setRows(rows.filter((row) => row.name !== name));
		setRowToEdit(null);
	};

	const processRowUpdate = (newRow) => {
		const updatedRow = { ...newRow, __reorder__: newRow.name, isNew: false };
		setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
		return updatedRow;
	};

	const handleRowModesModelChange = (newRowModesModel) => {
		setRowModesModel(newRowModesModel);
	};

	const handleRowOrderChange = (changeParams) => {
		const rowsClone = [...rows];
		const row = rowsClone.splice(changeParams.oldIndex, 1)[0];
		rowsClone.splice(changeParams.targetIndex, 0, row);
		setRows(rowsClone);
	};

	const inputTypeOptions = [
		{
			name: "Text",
			displayName: "Text",
			value: 1,
		},
		{
			name: "Number",
			displayName: "Number",
			value: 2,
		},
		{
			name: "Enumeration",
			displayName: "Single Selector",
			value: 3,
		},
		{
			name: "Boolean",
			displayName: "Toggle",
			value: 4,
		},
	];

	const columns = isEditMode
		? [
				{
					field: "name",
					headerName: "Name",
					width: 270,
					headerClassName: "new-param-theme--header",
					editable: false,
				},
				{
					field: "displayName",
					headerName: "Display Name",
					headerClassName: "new-param-theme--header",
					width: 290,
					editable: false,
				},
				{
					field: "type",
					headerName: "Type",
					headerClassName: "new-param-theme--header",
					width: 120,
					editable: false,
					renderCell: (params) => {
						const option = inputTypeOptions.find(
							(o) => o.value === params.value || o.name === params.value
						);
						return option ? option.displayName : "";
					},
				},
				{
					field: "uom",
					headerName: "UoM",
					headerClassName: "new-param-theme--header",
					width: 120,
					renderCell: (params) => {
						const option = uomOptions.find(
							(o) => o.abbreviation === params.value
						);
						return option ? option.unit : "N/A";
					},
					editable: false,
				},
				{
					field: "enumOptions",
					headerName: "Options",
					headerClassName: "new-param-theme--header",
					width: 200,
					editable: false,
					renderCell: (params) => {
						if (
							Array.isArray(params.value) &&
							typeof params.value[0] === "object"
						) {
							return params.value?.length > 0
								? params.value.map((option) => option.name).join(", ")
								: "N/A";
						}

						return params.value?.length > 0 ? params.value.join(", ") : "N/A";
					},
				},
				{
					field: "actions",
					type: "actions",
					headerName: "Actions",
					headerClassName: "new-param-theme--header",
					width: 80,
					editable: false,
					cellClassName: "actions",
					getActions: (params) => {
						return params.row.id || params.row.editable === false
							? [
									<GridActionsCellItem
										icon={<DeleteIcon color='error' />}
										label='Delete'
										onClick={handleDeleteClick(params.id)}
										color='inherit'
									/>,
							  ]
							: [
									<GridActionsCellItem
										icon={<EditIcon color='secondary' />}
										label='Edit'
										className='textPrimary'
										onClick={handleEditClick(params.id)}
										color='inherit'
									/>,
									<GridActionsCellItem
										icon={<DeleteIcon color='error' />}
										label='Delete'
										onClick={handleDeleteClick(params.id)}
										color='inherit'
									/>,
							  ];
					},
				},
		  ]
		: [
				{
					field: "name",
					headerName: "Name",
					width: 300,
					headerClassName: "new-param-theme--header",
					editable: false,
				},
				{
					field: "displayName",
					headerName: "Display Name",
					headerClassName: "new-param-theme--header",
					width: 320,
					editable: false,
				},
				{
					field: "type",
					headerName: "Type",
					headerClassName: "new-param-theme--header",
					width: 140,
					editable: false,
					renderCell: (params) => {
						const option = inputTypeOptions.find(
							(o) => o.value === params.value || o.name === params.value
						);
						return option ? option.displayName : "";
					},
				},
				{
					field: "uom",
					headerName: "UoM",
					headerClassName: "new-param-theme--header",
					width: 140,
					renderCell: (params) => {
						const option = uomOptions.find(
							(o) => o.abbreviation === params.value
						);
						return option ? option.unit : "N/A";
					},
					editable: false,
				},
				{
					field: "enumOptions",
					headerName: "Options",
					headerClassName: "new-param-theme--header",
					width: 200,
					editable: false,
					renderCell: (params) => {
						if (
							Array.isArray(params.value) &&
							typeof params.value[0] === "object"
						) {
							return params.value?.length > 0
								? params.value.map((option) => option.name).join(", ")
								: "N/A";
						}

						return params.value?.length > 0 ? params.value.join(", ") : "N/A";
					},
				},
		  ];

	return (
		<Box
			sx={{
				width: "100%",
				display: "flex",
				flexDirection: "column",
				minHeight: isProductDetailsPage ? '500px' : '400px',
				maxHeight: isProductDetailsPage ? '700px' : '800px',
				"& .new-param-theme--header": {
					color: "white",
				},
				"& .cell-error": {
					border: 1,
					borderColor: "red",
				},
				"& .actions": {
					color: "text.secondary",
				},
				"& .textPrimary": {
					color: "text.primary",
				},
			}}
		>
			<DataGridPro
				virtualizeColumnsWithAutoRowHeight
				rows={rows ?? []}
				initialState={{
					density: isProductDetailsPage && !isEditMode ? "compact" : "standard",
				}}
				getRowId={(row) => row.name}
				columns={columns}
				rowModesModel={rowModesModel}
				rowReordering={isEditMode}
				onRowOrderChange={handleRowOrderChange}
				onRowModesModelChange={handleRowModesModelChange}
				onRowEditStop={handleRowEditStop}
				processRowUpdate={processRowUpdate}
				slots={{ toolbar: EditToolbar }}
				sx={{ border: "0px !important", minWidth: minWidth }}
				slotProps={{
					toolbar: { setRows, setRowModesModel },
				}}
			/>
		</Box>
	);
}
